import React, { useState, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { getCurrentUser } from 'aws-amplify/auth';
import EventModal from './EventModal';
import EventDetailsModal from './EventDetailsModal';
import AvailabilityModal from './AvailabilityModal';
import EventTooltip from './EventTooltip';
import { getUserDisplayName } from '../utils/userMapping';
import './Calendar.css';

const API_URL = 'https://ryyy109uc3.execute-api.us-east-1.amazonaws.com/prod';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tooltipEvent, setTooltipEvent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [currentUser, setCurrentUser] = useState(null);
  const calendarRef = useRef(null);

  // Fetch events on component mount and get current user
  useEffect(() => {
    fetchEvents();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      console.log('Current user details:', user);
      setCurrentUser(user);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchEvents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Fetching events from API...');
      
      const response = await fetch(`${API_URL}/events`);
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Events data:', data);
      
      // Check if data is an array
      const eventsArray = Array.isArray(data) ? data : [];
      
      const formattedEvents = eventsArray.map(event => ({
        id: event.eventId,
        title: event.title,
        start: event.startTime,
        end: event.endTime,
        extendedProps: {
          description: event.description || '',
          location: event.location || '',
          eventType: event.eventType || 'other',
          createdBy: event.createdBy || '',
          creatorName: event.creatorName || getUserDisplayName(event.createdBy) || 'Band Member'
        },
        backgroundColor: getEventColor(event.eventType)
      }));
      
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError(`Failed to load events: ${error.message}`);
    }
    setIsLoading(false);
  };
  
  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'gig': return '#e74c3c'; // Red
      case 'practice': return '#3498db'; // Blue
      case 'recording': return '#9b59b6'; // Purple
      case 'social': return '#2ecc71'; // Green
      case 'unavailable': return '#95a5a6'; // Gray
      default: return '#f39c12'; // Orange
    }
  };

  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setSelectedEvent(null); // Clear any selected event
    setShowEventModal(true);
  };

  const handleEventClick = (info) => {
    setSelectedEvent({
      id: info.event.id,
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      description: info.event.extendedProps.description,
      location: info.event.extendedProps.location,
      eventType: info.event.extendedProps.eventType,
      createdBy: info.event.extendedProps.createdBy,
      creatorName: info.event.extendedProps.creatorName,
      extendedProps: info.event.extendedProps
    });
    setShowDetailsModal(true);
    // Hide tooltip when clicking on an event
    setTooltipEvent(null);
  };
  
  const handleEventMouseEnter = (info) => {
    const rect = info.el.getBoundingClientRect();
    
    // Calculate position relative to the viewport
    let top = rect.top + window.scrollY;
    let left = rect.left + window.scrollX;
    
    // Position the tooltip above the event by default
    top = top - 10; // Position above the event with a small gap
    
    // Make sure tooltip stays within viewport
    const viewportWidth = window.innerWidth;
    const tooltipWidth = 300; // Max width from CSS
    
    // If tooltip would go off right edge, align it to the right of the event
    if (left + tooltipWidth > viewportWidth) {
      left = rect.right - tooltipWidth + window.scrollX;
    }
    
    setTooltipPosition({
      top,
      left
    });
    setTooltipEvent(info.event);
  };
  
  const handleEventMouseLeave = () => {
    setTooltipEvent(null);
  };

  const handleCreateEvent = async (eventData) => {
    setIsLoading(true);
    setError(null);
    try {
      // Add the current user's username as the creator
      let username = 'unknown';
      let creatorName = 'Band Member';
      
      if (currentUser) {
        // Try to get username from different possible locations
        username = currentUser.username || 
                  currentUser.attributes?.email || 
                  currentUser.signInDetails?.loginId ||
                  'unknown';
        
        console.log('Using username for event:', username);
        creatorName = getUserDisplayName(username);
        console.log('Mapped to creator name:', creatorName);
      }
      
      // Update the event data with the creator information
      const updatedEventData = {
        ...eventData,
        createdBy: username,
        creatorName: creatorName
      };
      
      console.log('Creating event with data:', updatedEventData);
      
      // All events now go to the events endpoint
      const endpoint = `${API_URL}/events`;
      
      console.log(`Using endpoint: ${endpoint}`);
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(updatedEventData)
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      console.log("Event created successfully");
      fetchEvents(); // Refresh events after creating a new one
      setShowEventModal(false);
    } catch (error) {
      console.error('Error creating event:', error);
      setError(`Failed to create event: ${error.message}`);
    }
    setIsLoading(false);
  };

  const handleDeleteEvent = async (eventId) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Deleting event:', eventId);
      
      // All events now use the events endpoint
      const endpoint = `${API_URL}/events`;
      
      const bodyParam = { eventId: eventId };
      
      console.log(`Using endpoint: ${endpoint} with body:`, bodyParam);
      
      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(bodyParam)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log("Event deleted successfully:", result);
      
      fetchEvents(); // Refresh events after deleting
      setShowEventModal(false);
    } catch (error) {
      console.error('Error deleting event:', error);
      setError(`Failed to delete event: ${error.message}`);
    }
    setIsLoading(false);
  };
  
  const handleUpdateAvailability = async (availabilityData) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Updating availability:', availabilityData);
      
      const response = await fetch(`${API_URL}/availability`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(availabilityData)
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      console.log("Availability updated successfully");
      fetchEvents(); // Refresh events to show updated availability
      setShowAvailabilityModal(false);
    } catch (error) {
      console.error('Error updating availability:', error);
      setError(`Failed to update availability: ${error.message}`);
    }
    setIsLoading(false);
  };

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <h2>Calendar</h2>
        <div className="calendar-actions">
          <button 
            onClick={() => {
              setSelectedDate(new Date().toISOString().split('T')[0]);
              setSelectedEvent(null); // Clear any selected event
              setShowEventModal(true);
            }}
          >
            Add Event
          </button>
        </div>
      </div>
      
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="color-box" style={{backgroundColor: '#e74c3c'}}></span>
          <span>Gig</span>
        </div>
        <div className="legend-item">
          <span className="color-box" style={{backgroundColor: '#3498db'}}></span>
          <span>Practice</span>
        </div>
        <div className="legend-item">
          <span className="color-box" style={{backgroundColor: '#9b59b6'}}></span>
          <span>Recording</span>
        </div>
        <div className="legend-item">
          <span className="color-box" style={{backgroundColor: '#2ecc71'}}></span>
          <span>Social</span>
        </div>
        <div className="legend-item">
          <span className="color-box" style={{backgroundColor: '#95a5a6'}}></span>
          <span>Unavailable</span>
        </div>
      </div>
      
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="dismiss-button">Dismiss</button>
        </div>
      )}
      
      {isLoading ? (
        <div className="loading">Loading calendar...</div>
      ) : (
        <div className="calendar-wrapper">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            events={events}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            height="auto"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek'
            }}
          />
          {tooltipEvent && (
            <EventTooltip 
              event={tooltipEvent} 
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`
              }}
            />
          )}
        </div>
      )}
      
      {showEventModal && (
        <EventModal
          isOpen={showEventModal}
          onClose={() => {
            setShowEventModal(false);
            setSelectedEvent(null);
          }}
          onSave={handleCreateEvent}
          onDelete={handleDeleteEvent}
          selectedDate={selectedDate}
          event={selectedEvent}
        />
      )}
      
      {showDetailsModal && (
        <EventDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedEvent(null);
          }}
          onEdit={() => {
            setShowDetailsModal(false);
            setShowEventModal(true);
          }}
          event={selectedEvent}
        />
      )}
      
      {showAvailabilityModal && (
        <AvailabilityModal
          isOpen={showAvailabilityModal}
          onClose={() => setShowAvailabilityModal(false)}
          onSave={handleUpdateAvailability}
        />
      )}
    </div>
  );
};

export default Calendar;
